import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useProjekteUebersicht,
  useProjektStatusOptions,
  useSetProjektStatus,
} from "../hooks/usePlanung";
import type { ProjektUebersicht } from "../lib/types";
import {
  buildDefaultCollapsed,
  buildYearOptions,
  calendarYearNow,
  canonicalProjektStatus,
  normSearchField,
} from "../lib/projekte";
import PostZuPruefenLeiste from "../components/projekt/ProjektPost";
import ProjektNeuDialog from "../components/projekt/ProjektNeuDialog";
import StatusZurueckDialog from "../components/projekt/StatusZurueckDialog";
import ProjektGruppen from "../components/projekte/ProjektGruppen";
import ProjekteKennzahlen from "../components/projekte/ProjekteKennzahlen";
import type { StatusWahl } from "../components/projekte/ProjektKarte";
import { useProjektPhasen } from "../hooks/useProjektLage";
import { useIstTelefon } from "../hooks/useIstTelefon";
import "../styles/projekte.css";
import "../styles/phasen.css";
import "../styles/projektliste.css";

/** Merkt sich, ob die Kennzahlenleiste eingeblendet ist — Vorgabe seit 13.10.2026: aus. */
const KPI_SPEICHER = "kontor.projekte.kpiSichtbar";
/** Kacheln oder Liste am Rechner; das Telefon zeigt immer die Liste. */
const ANSICHT_SPEICHER = "kontor.projekte.ansicht";

function gemerkt(schluessel: string): string | null {
  try { return localStorage.getItem(schluessel); } catch { return null; }
}
function merken(schluessel: string, wert: string) {
  try { localStorage.setItem(schluessel, wert); } catch { /* privates Fenster: gilt dann nur jetzt */ }
}

/**
 * Projektübersicht — ursprünglich 1:1 aus apps/vimaana-desktop portiert.
 *
 * Reiter Bau/Pflege, Suche, Jahr, „Ohne Belege", klappbare Gruppen (Bau nach
 * Phase, Pflege nach Status). Seit 13.10.2026 zusätzlich als Liste — am Telefon
 * nur die Liste, damit möglichst viele Projekte auf den Schirm passen —, die
 * Kennzahlen standardmäßig aus, und die Suche geht immer über Bau UND Pflege und
 * über alle Jahre: wer einen Namen eintippt, weiß selten, in welcher Schublade
 * das Projekt liegt.
 *
 * Klick auf ein Projekt öffnet die Projektdetailseite (/projekte/:id).
 */
export default function ProjektePage() {
  const navigate = useNavigate();
  const telefon = useIstTelefon();
  const [selectedYear, setSelectedYear] = useState<number | "all">(calendarYearNow());
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const projekteQ = useProjekteUebersicht(selectedYear);
  const [searchQ, setSearchQ] = useState("");
  const suche = searchQ.trim() !== "";
  /* Beim Suchen alle Jahre: ein abgeschlossenes Projekt vom Vorjahr soll man finden,
     ohne erst „Alle Jahre" zu wählen. Bis die Liste da ist, sucht die Seite im Geladenen. */
  const alleJahreQ = useProjekteUebersicht("all", suche && selectedYear !== "all");
  /* Phase je laufendem Projekt (Etappe 3) — die Bauprojekte stehen danach gruppiert. */
  const phasen = useProjektPhasen().data;
  const statusOptsQ = useProjektStatusOptions();
  const setStatus = useSetProjektStatus(selectedYear);

  const projekte = useMemo(() => projekteQ.data ?? [], [projekteQ.data]);
  const statusOpts = useMemo(() => statusOptsQ.data ?? [], [statusOptsQ.data]);
  const loading = projekteQ.isPending && !projekteQ.data;
  const queryErr = projekteQ.error ?? statusOptsQ.error;
  const queryErrMsg = queryErr ? (queryErr instanceof Error ? queryErr.message : String(queryErr)) : null;
  const [err, setErr] = useState<string | null>(null);

  /** Filter „nur Projekte ohne zugewiesenes Angebot/Rechnung“. */
  const [nurOhneBelege, setNurOhneBelege] = useState(false);
  const [tab, setTab] = useState<"bau" | "pflege">("bau");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(buildDefaultCollapsed);
  const [showForm, setShowForm] = useState(false);
  const [filterOffen, setFilterOffen] = useState(false);
  const [kpiSichtbar, setKpiSichtbar] = useState(() => gemerkt(KPI_SPEICHER) === "1");
  const [ansicht, setAnsicht] = useState<"kacheln" | "liste">(() => (gemerkt(ANSICHT_SPEICHER) === "liste" ? "liste" : "kacheln"));
  const liste = telefon || ansicht === "liste";

  const istPflege = (p: ProjektUebersicht) => p.projektart === "pflege";
  const bauCount = useMemo(() => projekte.filter((p) => p.projektart !== "pflege").length, [projekte]);
  const pflegeCount = projekte.length - bauCount;
  /** Projekte des aktiven Reiters (vor Suchfilter) — Basis für die Kennzahlen. */
  const tabProjekte = useMemo(
    () => projekte.filter((p) => (tab === "pflege" ? p.projektart === "pflege" : p.projektart !== "pflege")),
    [projekte, tab],
  );

  /** Ohne Suche: der aktive Reiter. Mit Suche: Bau und Pflege, alle Jahre. */
  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    let base = q ? (alleJahreQ.data ?? projekte) : tabProjekte;
    if (q) {
      base = base.filter((p) =>
        normSearchField(p.title).includes(q)
        || normSearchField(p.kunde_name).includes(q)
        || normSearchField(p.ort).includes(q)
        || normSearchField(p.verantwortlicher).includes(q));
    }
    if (nurOhneBelege) base = base.filter((p) => !(p.hat_angebot || p.hat_rechnung));
    return base;
  }, [tabProjekte, projekte, alleJahreQ.data, searchQ, nurOhneBelege]);

  const aktiv = useMemo(
    () => projekte.filter((p) => canonicalProjektStatus(p.status, statusOpts) === "🟢 aktiv").length,
    [projekte, statusOpts],
  );

  // Zurück auf Anfrage fragt nach (12.10.2026) — sonst setzt die Phase den Status gleich wieder.
  const [zurueck, setZurueck] = useState<{ id: string; titel: string } | null>(null);
  const wahl: StatusWahl = {
    optionen: statusOpts,
    wechseltGerade: (id) => setStatus.isPending && setStatus.variables?.id === id,
    aendern: (id, neu) => {
      setErr(null);
      const p = projekte.find((x) => x.id === id) ?? alleJahreQ.data?.find((x) => x.id === id);
      if (p && neu.includes("anfrage") && /aktiv|beauftragt|rechnung/.test(p.status || "")) {
        setZurueck({ id, titel: p.title || "" });
        return;
      }
      setStatus.mutate({ id, status: neu }, { onError: (e) => setErr(e instanceof Error ? e.message : String(e)) });
    },
  };

  const gruppen = (items: ProjektUebersicht[], art: "bau" | "pflege") => (
    <ProjektGruppen
      items={items}
      art={art}
      phasen={phasen}
      wahl={wahl}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      alleOffen={suche}
      liste={liste}
      leerText={suche ? "Keine Projekte gefunden" : "Noch keine Projekte vorhanden"}
    />
  );

  /** Suchtreffer in zwei Abschnitten — die Reiter treten beim Suchen zurück. */
  const renderTreffer = () => {
    const bau = filtered.filter((p) => !istPflege(p));
    const pflege = filtered.filter(istPflege);
    if (bau.length + pflege.length === 0) return <div className="vg-empty">Keine Projekte gefunden</div>;
    return (
      <>
        {bau.length > 0 && <h2 className="vg-proj-abschnitt">Bauprojekte <span>{bau.length}</span></h2>}
        {bau.length > 0 && gruppen(bau, "bau")}
        {pflege.length > 0 && <h2 className="vg-proj-abschnitt">Pflegeprojekte <span>{pflege.length}</span></h2>}
        {pflege.length > 0 && gruppen(pflege, "pflege")}
      </>
    );
  };

  const jahrWahl = (
    <label className="vg-proj-year-filter">
      <span className="vg-proj-year-label">Jahr</span>
      <select
        className="vg-proj-year-select"
        value={selectedYear === "all" ? "all" : String(selectedYear)}
        onChange={(e) => setSelectedYear(e.target.value === "all" ? "all" : Number(e.target.value))}
        aria-label="Projektliste nach Jahr filtern"
      >
        {yearOptions.map((y) => (
          <option key={String(y)} value={String(y)}>{y === "all" ? "Alle Jahre" : String(y)}</option>
        ))}
      </select>
    </label>
  );
  const ohneBelegeKnopf = (
    <button
      type="button"
      className={"vg-proj-tab" + (nurOhneBelege ? " is-active" : "")}
      onClick={() => setNurOhneBelege((v) => !v)}
      title="Nur Projekte ohne zugewiesenes Angebot oder Rechnung anzeigen"
      aria-pressed={nurOhneBelege}
    >
      Ohne Belege
    </button>
  );
  const filterAbweichend = nurOhneBelege || selectedYear !== calendarYearNow();

  return (
    <div className="projekte-page vg-projekte">
      <div className="vg-page-head">
        <div className="vg-page-title">
          <span className="vg-page-glyph">▣</span>
          <h1>Projekte</h1>
          <span className="vg-page-count">{projekte.length} Projekte · {aktiv} aktiv</span>
        </div>
        <div className="vg-page-actions">
          <button type="button" className="vg-btn vg-btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Abbrechen" : "+ Neues Projekt"}
          </button>
        </div>
      </div>

      {(err || queryErrMsg) && <div className="vg-banner is-warn">{err || queryErrMsg}</div>}

      {/* Dieselbe Maske wie überall (13.10.2026): Projekt und — falls nötig — Kunde mit
          vollständigen Stammdaten und Adresssuche. */}
      {showForm && (
        <ProjektNeuDialog
          hinweis="danach geht es direkt ins Projekt"
          onSchliessen={() => setShowForm(false)}
          onAngelegt={(p) => { setShowForm(false); navigate(`/projekte/${p.id}`); }}
        />
      )}

      <PostZuPruefenLeiste />

      {!loading && projekte.length > 0 && (
        <>
          {/* Suche zuerst: am Telefon der schnellste Weg zum Projekt. Daneben dort
              Filter und „+", die Kopfzeile der Seite fällt weg. */}
          <div className="vg-proj-toolbar">
            <div className="vg-proj-search">
              <span className="vg-proj-search-glyph" aria-hidden="true">⌕</span>
              <input
                placeholder={telefon ? "Projekt, Kunde, Ort …" : "Projekt, Kunde, Ort oder Verantwortlicher suchen…"}
                aria-label="Projekte durchsuchen"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                type="search"
                name="projekt-suche"
                autoComplete="off"
                spellCheck={false}
              />
              {searchQ && (
                <button className="vg-proj-search-clear" onClick={() => setSearchQ("")} title="Suche leeren" aria-label="Suche leeren" type="button">✕</button>
              )}
            </div>
            <span className="vg-desk-nur vg-proj-toolbar-rest">
              {ohneBelegeKnopf}
              {jahrWahl}
              <span className="vg-proj-ansicht" role="group" aria-label="Ansicht">
                {(["kacheln", "liste"] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    className={ansicht === a ? "is-aktiv" : ""}
                    aria-pressed={ansicht === a}
                    onClick={() => { setAnsicht(a); merken(ANSICHT_SPEICHER, a); }}
                  >
                    {a === "kacheln" ? "Kacheln" : "Liste"}
                  </button>
                ))}
              </span>
            </span>
            <button type="button" className="vg-symbolknopf vg-tel-nur" aria-label={filterAbweichend ? "Filter (aktiv)" : "Filter"} onClick={() => setFilterOffen(true)}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 6h16M7 12h10M10 18h4" />
              </svg>
              {filterAbweichend && <i className="vg-filterpunkt" aria-hidden="true" />}
            </button>
            <button type="button" className="vg-symbolknopf vg-symbolknopf-haupt vg-tel-nur" aria-label="Neues Projekt" onClick={() => setShowForm(true)}>＋</button>
          </div>

          {!suche && (
            <div className="vg-proj-tabs vg-proj-reiter" role="tablist" aria-label="Projektart">
              <button type="button" role="tab" aria-selected={tab === "bau"} className={"vg-proj-tab" + (tab === "bau" ? " is-active" : "")} onClick={() => setTab("bau")}>
                Bauprojekte <span className="vg-proj-tab-count">{bauCount}</span>
              </button>
              <button type="button" role="tab" aria-selected={tab === "pflege"} className={"vg-proj-tab" + (tab === "pflege" ? " is-active" : "")} onClick={() => setTab("pflege")}>
                Pflegeprojekte <span className="vg-proj-tab-count">{pflegeCount}</span>
              </button>
              <button
                type="button"
                className="vg-proj-strip-toggle vg-desk-nur"
                onClick={() => setKpiSichtbar((v) => { merken(KPI_SPEICHER, v ? "0" : "1"); return !v; })}
                aria-expanded={kpiSichtbar}
              >
                {kpiSichtbar ? "Zahlen ausblenden" : "Zahlen einblenden"}
              </button>
            </div>
          )}
          {!suche && kpiSichtbar && <div className="vg-desk-nur"><ProjekteKennzahlen projekte={tabProjekte} statusOpts={statusOpts} /></div>}
        </>
      )}

      {loading ? (
        <div className="vg-empty">Lade Projekte…</div>
      ) : projekte.length === 0 ? (
        <div className="vg-empty">Noch keine Projekte vorhanden. Lege dein erstes Projekt mit „+ Neues Projekt“ an.</div>
      ) : suche ? (
        renderTreffer()
      ) : (
        <>
          {selectedYear !== "all" && (
            <p className="vg-proj-year-hint vg-desk-nur">
              Laufende Projekte (Anfrage, Aktiv, Rechnung, …) werden immer angezeigt; abgeschlossene und
              verlorene nur mit Start-, Abschluss- oder Änderungsjahr {selectedYear}. Für die Gesamtliste
              „Alle Jahre“ wählen. Die Suche findet alle Jahre.
            </p>
          )}
          {gruppen(filtered, tab)}
        </>
      )}

      {filterOffen && (
        <div className="kontor-sheet-backdrop" onClick={() => setFilterOffen(false)}>
          <div className="kontor-sheet vg-proj-filterblatt" role="dialog" aria-modal="true" aria-label="Projekte filtern" onClick={(e) => e.stopPropagation()}>
            <div className="kontor-sheet-griff" aria-hidden="true" />
            {jahrWahl}
            {ohneBelegeKnopf}
            <button type="button" className="kontor-sheet-close" onClick={() => setFilterOffen(false)}>Fertig</button>
          </div>
        </div>
      )}
      {zurueck && <StatusZurueckDialog projektId={zurueck.id} titel={zurueck.titel} onClose={() => setZurueck(null)} />}
    </div>
  );
}
